import { ensureFreshSession, getAccessToken, refreshSession } from "./auth.js";

export type SseHandler = (event: string, data: string) => void;

function applyAuthHeaders(headers: Headers) {
  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("Accept", "text/event-stream");
  headers.set("Cache-Control", "no-cache");
  headers.set("X-No-Compression", "1");
}

/** Parse one SSE frame (event + data lines). Exported for tests. */
export function parseSseFrame(
  frame: string,
): { event: string; data: string } | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const rawLine of frame.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:"))
      dataLines.push(line.slice(5).trimStart());
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}

export async function readSseBody(
  body: ReadableStream<Uint8Array>,
  onEvent: SseHandler,
  signal?: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const abort = () => {
    void reader.cancel().catch(() => {});
  };
  signal?.addEventListener("abort", abort, { once: true });
  try {
    while (!signal?.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        const parsed = parseSseFrame(frame);
        if (!parsed || parsed.event === "ping") continue;
        onEvent(parsed.event, parsed.data);
      }
    }
  } finally {
    signal?.removeEventListener("abort", abort);
    reader.releaseLock();
  }
}

/**
 * Open an authenticated SSE stream for generate/build.
 * Sends the session JWT only in the Authorization header so credentials never
 * appear in URLs, logs, browser history, or referrers. Refreshes once on 401.
 */
export async function consumeAuthedSse(
  path: string,
  onEvent: SseHandler,
  signal?: AbortSignal,
): Promise<void> {
  await ensureFreshSession();

  const open = async (retried: boolean): Promise<Response> => {
    // Do not require a browser-readable bearer token here. AppForge keeps the
    // refresh token in an HttpOnly cookie, and the server auth middleware can
    // refresh an expired/missing access token from that cookie. Requiring a
    // bearer token before fetch prevented exactly that secure refresh path.
    const headers = new Headers();
    applyAuthHeaders(headers);
    const res = await fetch(path, {
      method: "GET",
      headers,
      credentials: "same-origin",
      signal,
      cache: "no-store",
    });
    if (res.status === 401 && !retried) {
      const session = await refreshSession();
      if (session) return open(true);
    }
    return res;
  };

  const res = await open(false);
  if (signal?.aborted) return;
  if (res.status === 401) {
    const err = new Error("Not authenticated");
    (err as Error & { status?: number }).status = 401;
    throw err;
  }
  if (!res.ok) {
    let message = `Build stream failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      message = body.message || body.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  if (!res.body) {
    throw new Error("Build stream had no body");
  }
  await readSseBody(res.body, onEvent, signal);
}
